import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useCallback, useContext } from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useSaveHotelInventory } from 'app/hooks/data/hotels/extranet/useInventory';
import { useRooms } from 'app/hooks/data/useRooms';
import { ChannelManager } from 'app/typings/channelManagers';
import { RateMode } from 'app/typings/rateModes';
import {
  getChannelManagerLabel,
  getChannelManagerLink,
} from 'app/utils/channelManagers/channelManagers';
import { formatDateWithoutHour } from 'app/utils/dates';
import { format } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';
import { assertNever } from 'app/utils/typing';

import { inventoryStore } from '../../InventoryStore';
import { PricingRuleBulkEditAlert } from '../../PricingRule/_components/PricingRuleBulkEditAlert';
import { OpeningErrors } from '../../type';

import './ValidationModal.scss';

type Props = {
  autoDiscountRate?: number;
};

export const ValidationModal = ({ autoDiscountRate }: Props) => {
  const setInventoryErrors = useStore(
    inventoryStore,
    (state) => state.setInventoryErrors
  );
  const inventoryErrors = useStore(
    inventoryStore,
    (state) => state.inventoryErrors
  );
  const timePeriod = useStore(inventoryStore, (state) => state.timePeriod);
  const setSaveChangesModal = useStore(
    inventoryStore,
    (state) => state.setSaveChangesModal
  );
  const setFormHasChanged = useStore(
    inventoryStore,
    (state) => state.setFormHasChanged
  );
  const setIsEditMode = useStore(
    inventoryStore,
    (state) => state.setIsEditMode
  );
  const inventorySaved = useStore(
    inventoryStore,
    (state) => state.inventorySaved
  );

  const { hotelId, hotelInfo } = useContext(HotelContext);
  const { packages } = useContext(LegacyHotelContext);
  const { isAdmin } = useContext(UserContext);
  const { data: rooms } = useRooms(hotelId);

  const { mutateAsync: saveInventory, isPending } = useSaveHotelInventory(
    hotelId,
    formatDateWithoutHour(timePeriod.startDate)
  );

  const shouldEditOnChannelManager =
    !isAdmin && hotelInfo?.rateMode === RateMode.MULTI_RATE;

  const errorListCount = inventoryErrors.reduce(
    (acc, err) => acc + err.openings.length,
    0
  );

  const onResolve = useCallback(() => {
    amplitudeTrack('Click Resolve Errors', {
      nbErrors: errorListCount,
    });

    if (shouldEditOnChannelManager && hotelInfo?.channelManager) {
      window.location.replace(getChannelManagerLink(hotelInfo.channelManager));
    } else {
      setInventoryErrors([]);
    }

    setSaveChangesModal({
      isVisible: false,
      timePeriod: { startDate: new Date(), endDate: new Date() },
    });
  }, [
    errorListCount,
    hotelInfo?.channelManager,
    setInventoryErrors,
    setSaveChangesModal,
    shouldEditOnChannelManager,
  ]);

  const onIgnore = useCallback(async () => {
    amplitudeTrack('Click Ignore Errors', {
      nbErrors: errorListCount,
    });

    await saveInventory(inventorySaved);
    setIsEditMode(false);
    setFormHasChanged(false);
    setInventoryErrors([]);
  }, [
    errorListCount,
    saveInventory,
    inventorySaved,
    setIsEditMode,
    setFormHasChanged,
    setInventoryErrors,
  ]);

  const getErrorsWording = (errorType: OpeningErrors, nbErrors: number) => {
    switch (errorType) {
      case OpeningErrors.EXCEED_RATE:
        return pluralize(
          nbErrors,
          'Staycation rate is above maximum accepted',
          'Staycation rates are above maximum accepted'
        );
      case OpeningErrors.CLOSED:
        if (hotelInfo?.stockScrapperEnabled) {
          return pluralize(
            nbErrors,
            'availability has a stop sell',
            'availabilities have a stop sell'
          );
        }

        return pluralize(
          nbErrors,
          'availability is closed on your channel manager',
          'availabilities are closed on your channel manager'
        );
      case OpeningErrors.MISSING_BAR:
        return pluralize(nbErrors, 'BAR RO is missing', 'BAR RO are missing');
      case OpeningErrors.MISSING_DISCOUNT_PRICE:
        return pluralize(
          nbErrors,
          'Staycation rate is missing',
          'Staycation rates are missing'
        );
      case OpeningErrors.MISSING_STOCK:
        return pluralize(
          nbErrors,
          'remaining stock is missing',
          'remaining stocks are missing'
        );
      case OpeningErrors.NO_DISCOUNT:
        return pluralize(
          nbErrors,
          'discount is missing',
          'discounts are missing'
        );
      case OpeningErrors.UNDER_MINIMUM_PRICE:
        return pluralize(
          nbErrors,
          'Staycation rate is under minimum accepted',
          'Staycation rates are under minimum accepted'
        );
      case OpeningErrors.WRONG_STOCK:
        return pluralize(
          nbErrors,
          'remaining stock is above maximum accepted',
          'remaining stock are above maximum accepted'
        );
      case OpeningErrors.REJECTED_BY_AM:
        return pluralize(
          nbErrors,
          'Opening is rejected by your account manager',
          'Openings are rejected by your account manager'
        );

      default:
        assertNever(errorType);
    }
  };

  return (
    <Modal
      width={750}
      open={inventoryErrors.length > 0}
      title={`${pluralize(
        errorListCount,
        'availability',
        'availabilities'
      )} won't be validated`}
      onOk={onResolve}
      maskClosable={false}
      onCancel={onIgnore}
      closable={false}
      className="extranet-inventory-modal"
      footer={[
        <Button onClick={onIgnore} loading={isPending}>
          Ignore
        </Button>,
        <Button type="primary" onClick={onResolve}>
          {hotelInfo?.channelManager &&
          hotelInfo?.channelManager !== ChannelManager.NONE &&
          shouldEditOnChannelManager
            ? `Edit on ${getChannelManagerLabel(hotelInfo.channelManager)}`
            : 'Resolve'}
        </Button>,
      ]}
      bodyStyle={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
      centered
      destroyOnClose
    >
      {inventoryErrors.map((error) => (
        <div key={error.errorType}>
          <h3>
            <span className="extranet-inventory-modal__danger">
              <ExclamationCircleFilled />
              {getErrorsWording(error.errorType, error.openings.length)}
            </span>
          </h3>
          <ul>
            {error.openings.map((op) => {
              // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
              const room = rooms?.find((room) => room.id === op.roomId);

              const packageName = packages.find(
                (pkg) => pkg.id === op.packageId
              )?.aliasForHotel;

              return (
                <li key={`${op.roomId}-${op.packageId}-${op.date}`}>
                  {`${room?.name ?? room?.categoryName} - ${
                    packageName ? `${packageName} -` : ''
                  } ${format(new Date(op.date), 'ddd DD/MM')}`}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {autoDiscountRate && <PricingRuleBulkEditAlert />}
    </Modal>
  );
};
